import React, { useState, useEffect, useContext } from "react";
import { Fragment, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { EyeIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import AddSale from "../components/AddSale";
import { can, Permissions, hasPolicy, Policies } from "../auth/authorization";
import AuthContext from "../AuthContext";
import { apiFetch } from "../api/client";
import { generateSalePDF } from "../utils/generateSalePDF";
import { useNotification } from "../components/Notifications";
import { hasRole, Roles } from "../auth/authorization";
import { DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import { formatNairaInput } from "../utils/currencyInput";

function Sales() {
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [sales, setAllSalesData] = useState([]);
  const [products, setAllProducts] = useState([]);
  const [stores, setAllStores] = useState([]);
  const [updatePage, setUpdatePage] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewingSale, setViewingSale] = useState(null);
  const [editingSale, setEditingSale] = useState(null);
  const [deletingSaleId, setDeletingSaleId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const cancelButtonRef = useRef(null);
  const canManageSales = hasRole(Roles.StokrazeUser);
  const [expandedRows, setExpandedRows] = useState({});
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const authContext = useContext(AuthContext);
  const notify = useNotification();

  const parseNumber = (v) => {
    if (v === null || v === undefined) return 0;
    const s = String(v).replace(/[^0-9.-]+/g, "");
    const n = Number(s);
    return Number.isNaN(n) ? 0 : n;
  };

  const formatCurrency = (v) => {
    const n = parseNumber(v ?? 0);
    return `\u20A6${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const toggleRow = (saleId) => {
  setExpandedRows((prev) => ({
    ...prev,
    [saleId]: !prev[saleId],
  }));
};

useEffect(() => {
  fetchSalesData(pageNumber, pageSize);
}, [pageNumber]);
  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchSalesData(),
      fetchProductsData(),
      fetchStoresData(),
      fetchSubscriptionStatus(),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatePage]);

  // Fetching Data of All Sales
 const fetchSalesData = async (pageNumber = 1, pageSize = 10) => {
  try {
    const res = await apiFetch(
      `/api/sale?pageNumber=${pageNumber}&pageSize=${pageSize}`
    );

    if (!res.ok) return;

    const data = await res.json();

    const result = data?.data || data?.Data;

  setAllSalesData(result?.items || []);
  setTotalPages(result?.totalPages || 1);
  } catch (err) {
    console.log(err);
  }
};

  // Fetching Data of All Products
  const fetchProductsData = async () => {
    try {
      const res = await apiFetch(`/api/product`);
      if (!res.ok) return;
      const data = await res.json();
      const arr = Array.isArray(data) ? data : (data?.data || data?.Data || []);
      setAllProducts(arr || []);
    } catch (err) {
      console.log(err);
    }
  };

  // Fetching Data of All Vendors (stores)
  const fetchStoresData = async () => {
    try {
      const res = await apiFetch(`/api/vendor`);
      if (!res.ok) return;
      const data = await res.json();
      const arr = Array.isArray(data) ? data : (data?.data || data?.Data || []);
      setAllStores(arr || []);
    } catch (err) {
      console.log(err);
    }
  };

  // Fetching Current Subscription Status
  const fetchSubscriptionStatus = async () => {
    try {
      const res = await apiFetch(`/api/subscription`);
      if (!res.ok) return;
      const data = await res.json();
      const plan = data?.plan || null;
      
      // Check if plan is premium
      const planName = (plan?.name || plan?.Name || '').toLowerCase();
      const premium = planName.includes('premium');
      setIsPremium(premium);
    } catch (err) {
      console.log('Error fetching subscription:', err);
      setIsPremium(false);
    }
  };

  // Handle PDF Download
  const handleDownloadPDF = async (sale) => {
    setPdfLoading(true);
    try {
      // Get tenant name from localStorage
      const tenant = localStorage.getItem('tenant') || 'Stokraze';
      
      // Find product name
const productName =
  sale.items?.length > 1
    ? `${sale.items.length} Items`
    : sale.productName ||
      sale.items?.[0]?.productName ||
      "Unknown Product";      
      await generateSalePDF(sale, productName, tenant);
      notify("success", {
        title: "PDF Generated",
        text: "Sales receipt downloaded successfully.",
      });
    } catch (err) {
      console.error('Error downloading PDF:', err);
      notify("error", {
        title: "PDF Generation Failed",
        text: "Failed to generate PDF. Please try again.",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  // Handle View Sale
  const handleViewSale = (sale) => {
    setViewingSale(sale);
  };

  // Handle Edit Sale
  const handleEditSale = (sale) => {
  if (sale.items && sale.items.length > 1) {
    notify("error", {
      title: "Unable to edit",
      text: "Multi-item sales cannot be edited. Please delete the sale and create a new one.",
    });

    return;
  }

  setEditingSale(sale);

  setEditFormData({
    stockSold: sale.items
      ? sale.items.reduce(
          (a, b) => a + (b.quantity || b.Quantity || 0),
          0
        )
      : sale.StockSold || 0,
    paymentMethod:
      sale.paymentMethod || sale.PaymentMethod || "Cash",
    saleDate: sale.saleDate || sale.SaleDate || "",
    total: formatNairaInput(sale.total ?? sale.Total ?? 0),
  });
};

  // Handle Delete Sale - Show Confirmation
  const handleDeleteSale = (saleId) => {
    setDeletingSaleId(saleId);
  };

  // Confirm Delete
  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      const res = await apiFetch(`/api/sale/${deletingSaleId}`, { method: 'DELETE' });
      if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        const errorMsg = errorData?.message || 'Failed to delete sale';
        notify("error", {
          title: "Delete Failed",
          text: errorMsg,
        });
        setIsDeleting(false);
        return;
      }
      setDeletingSaleId(null);
      notify("success", {
        title: "Sale Deleted",
        text: "Sale has been deleted successfully.",
      });
      handlePageUpdate();
    } catch (err) {
      console.error('Error deleting sale:', err);
      notify("error", {
        title: "Delete Error",
        text: "Error deleting sale: " + err.message,
      });
      setIsDeleting(false);
    }
  };

  // Cancel Delete
  const cancelDelete = () => {
    setDeletingSaleId(null);
    setIsDeleting(false);
  };

  // Close View Modal
  const closeViewModal = () => {
    setViewingSale(null);
  };

  // Close Edit Modal
  const closeEditModal = () => {
    setEditingSale(null);
    setEditFormData({});
    setIsUpdating(false);
  };

  // Handle Edit Form Change
  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  // Save Edit
  const saveEdit = async () => {
    setIsUpdating(true);
    try {
      // Get product ID from the sale data
      const productId = editingSale.productId || editingSale.ProductId || editingSale.items?.[0]?.productId || '';
      
      // Calculate unit price
      const quantity = Number(editFormData.stockSold) || 0;
      const total = parseNumber(editFormData.total) || 0;
      const unitPrice = quantity ? total / quantity : 0;

      // Prepare the payload matching the API expectations (same as AddSale)
      const payload = {
        Items: [
          {
            ProductId: productId,
            Quantity: quantity,
            UnitPrice: unitPrice,
          },
        ],
        PaymentMethod: editFormData.paymentMethod,
      };

      const saleId = editingSale.saleId || editingSale.SaleId;
      const res = await apiFetch(`/api/sale/${saleId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        const errorMsg = errorData?.message || 'Failed to update sale';
        notify("error", {
          title: "Update Failed",
          text: errorMsg,
        });
        setIsUpdating(false);
        return;
      }

      closeEditModal();
      notify("success", {
        title: "Sale Updated",
        text: "Sale has been updated successfully.",
      });
      handlePageUpdate();
    } catch (err) {
      console.error('Error updating sale:', err);
      notify("error", {
        title: "Update Error",
        text: "Error updating sale: " + err.message,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Modal for Sale Add
  const addSaleModalSetting = () => {
    setShowSaleModal(!showSaleModal);
  };

  // Handle Page Update
  const handlePageUpdate = () => {
    setUpdatePage(!updatePage);
  };

  return (
    <div className="col-span-12 lg:col-span-10  flex justify-center">
      <div className=" flex flex-col gap-5 w-11/12">
        {showSaleModal && hasPolicy(Policies.Create) && (
          <AddSale
            addSaleModalSetting={addSaleModalSetting}
            products={products}
            stores={stores}
            handlePageUpdate={handlePageUpdate}
            authContext={authContext}
          />
        )}
        {/* Table  */}
        <div className="overflow-x-auto rounded-lg border bg-white border-gray-200 ">
          <div className="flex justify-between pt-5 pb-3 px-3">
            <div className="flex gap-4 justify-center items-center ">
              <span className="font-bold">Sales</span>
            </div>
            <div className="flex gap-4">
              {hasPolicy(Policies.Create) && (
                <button
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold p-2 text-xs  rounded"
                  onClick={addSaleModalSetting}
                >
                  Add Sales
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
              <>
            <table className="min-w-full divide-y-2 divide-gray-200 text-sm">
            <thead>
              <tr>
                <th className="whitespace-nowrap px-4 py-2 text-left font-medium text-gray-900">
                  Sales Id
                </th>
                <th className="whitespace-nowrap px-4 py-2 text-left font-medium text-gray-900">
                  Product Name
                </th>
                <th className="whitespace-nowrap px-4 py-2 text-left font-medium text-gray-900">
                  Stock Sold
                </th>
                <th className="whitespace-nowrap px-4 py-2 text-left font-medium text-gray-900">
                  Payment Method
                </th>
                <th className="whitespace-nowrap px-4 py-2 text-left font-medium text-gray-900">
                  Sales Date
                </th>
                <th className="whitespace-nowrap px-4 py-2 text-left font-medium text-gray-900">
                  Total Sale Amount
                </th>
                <th className="whitespace-nowrap px-4 py-2 text-left font-medium text-gray-900">
                  Actions
                </th>
              </tr>
            </thead>

           <tbody className="divide-y divide-gray-200">
  {sales.map((element, index) => {
    const saleId = element.saleId || element.SaleId;
    const items = element.items || element.Items || [];

    const totalQuantity =
      items.length > 0
        ? items.reduce(
            (sum, item) =>
              sum + Number(item.quantity || item.Quantity || 0),
            0
          )
        : element.StockSold || 0;

    return (
      <Fragment key={saleId || index}>
        <tr>
          <td className="whitespace-nowrap px-4 py-2 text-gray-900">
            {saleId}
          </td>

          <td className="px-4 py-2 text-gray-700">
            {items.length > 0 ? (
              <div className="flex items-center gap-2">
                <span>{items.length} Item(s)</span>

                <button
                  onClick={() => toggleRow(saleId)}
                  className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                >
                  {expandedRows[saleId]
                    ? "Hide Items"
                    : "View Items"}
                </button>
              </div>
            ) : (
              element.productName ||
              element.ProductName ||
              "N/A"
            )}
          </td>

          <td className="whitespace-nowrap px-4 py-2 text-gray-700">
            {totalQuantity}
          </td>

          <td className="whitespace-nowrap px-4 py-2 text-gray-700">
            {element.paymentMethod ||
              element.PaymentMethod ||
              "Cash"}
          </td>

         <td className="whitespace-nowrap px-4 py-2 text-gray-700">
              {new Date(
                element.saleDate || element.SaleDate
              ).toLocaleDateString()}
            </td>

          <td className="whitespace-nowrap px-4 py-2 text-gray-700">
            {formatCurrency(
              element.total ?? element.Total ?? 0
            )}
          </td>

          <td className="whitespace-nowrap px-4 py-2">
  <div className="flex items-center gap-3">

    {isPremium && (
      <button
        onClick={() => handleDownloadPDF(element)}
        disabled={pdfLoading}
        title="Download PDF"
        className="text-purple-600 hover:text-purple-800 disabled:opacity-50"
      >
        <DocumentArrowDownIcon className="h-5 w-5" />
      </button>
    )}

    {can(Permissions.View) && (
      <button
        onClick={() => handleViewSale(element)}
        title="View Sale"
        className="text-blue-600 hover:text-blue-800"
      >
        <EyeIcon className="h-5 w-5" />
      </button>
    )}

    {canManageSales && (
      <button
        onClick={() => handleEditSale(element)}
        title="Edit Sale"
        className="text-green-600 hover:text-green-800"
      >
        <PencilIcon className="h-5 w-5" />
      </button>
    )}

    {canManageSales && (
      <button
        onClick={() =>
          handleDeleteSale(element.saleId || element.SaleId)
        }
        title="Delete Sale"
        className="text-red-600 hover:text-red-800"
      >
        <TrashIcon className="h-5 w-5" />
      </button>
    )}

  </div>
</td>
        </tr>

        {expandedRows[saleId] && items.length > 0 && (
          <tr>
            <td colSpan="7" className="bg-gray-50 px-6 py-4">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Product</th>
                      <th className="text-left py-2">Quantity</th>
                      <th className="text-left py-2">Unit Price</th>
                      <th className="text-left py-2">Line Total</th>
                    </tr>
                  </thead>

                  <tbody>
                    {items.map((item, idx) => {
                      const qty =
                        item.quantity ||
                        item.Quantity ||
                        0;

                      const price =
                        item.unitPrice ||
                        item.UnitPrice ||
                        0;

                      return (
                        <tr
                          key={idx}
                          className="border-b last:border-b-0"
                        >
                          <td className="py-2">
                            {item.productName ||
                              item.ProductName ||
                              `Product ${idx + 1}`}
                          </td>

                          <td className="py-2">
                            {qty}
                          </td>

                          <td className="py-2">
                            {formatCurrency(price)}
                          </td>

                          <td className="py-2 font-medium">
                            {formatCurrency(
                              qty * price
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </td>
          </tr>
        )}
      </Fragment>
    );
  })}
</tbody>
          </table>
           {/* ✅ PAGINATION ADDED HERE (NO CHANGES TO YOUR CODE ABOVE) */}
            <div className="flex items-center justify-between px-4 py-4 border-t bg-white">

              <button
                onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                disabled={pageNumber === 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Previous
              </button>

              <span className="text-sm text-gray-600">
                Page {pageNumber} of {totalPages}
              </span>

              <button
                onClick={() =>
                  setPageNumber((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={pageNumber === totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Next
              </button>

            </div>
          </>
          
          )}
        </div>
      </div>

      {/* View Sale Modal */}
      <Transition.Root show={viewingSale !== null} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          initialFocus={cancelButtonRef}
          onClose={closeViewModal}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                        <EyeIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                      </div>
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                        <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                          Sale Details
                        </Dialog.Title>
                        {viewingSale && (
                          <div className="mt-4 space-y-3">
                            <div>
                              <label className="text-sm font-medium text-gray-700">Sales ID</label>
                              <p className="mt-1 text-gray-900">{viewingSale.saleId || viewingSale.SaleId}</p>
                            </div>
                            <div>
                            <label className="text-sm font-medium text-gray-700">Items Sold</label>

                            {viewingSale.items?.length > 0 ? (
                              <div className="mt-2 border rounded-lg divide-y">
                                {viewingSale.items.map((item, index) => (
                                  <div
                                    key={index}
                                    className="flex justify-between items-center p-3"
                                  >
                                    <div>
                                      <p className="font-medium text-gray-900">
                                        {item.productName || item.ProductName}
                                      </p>

                                      <p className="text-xs text-gray-500">
                                        Qty: {item.quantity || item.Quantity}
                                      </p>
                                    </div>

                                    <div className="font-semibold text-green-600">
                                      ₦{(
                                        (item.quantity || item.Quantity || 0) *
                                        (item.unitPrice || item.UnitPrice || 0)
                                      ).toLocaleString()}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="mt-1 text-gray-900">
                                {viewingSale.productName || "N/A"}
                              </p>
                            )}
                          </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">Stock Sold</label>
                              <p className="mt-1 text-gray-900">
                                {viewingSale.items 
                                  ? viewingSale.items.reduce((a, b) => a + (b.quantity || b.Quantity || 0), 0)
                                  : viewingSale.StockSold}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">Payment Method</label>
                              <p className="mt-1 text-gray-900">{viewingSale.paymentMethod || viewingSale.PaymentMethod || 'Cash'}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">Sale Date</label>
                              <p className="mt-1 text-gray-900">{viewingSale.saleDate || viewingSale.SaleDate}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">Total Amount</label>
                              <p className="mt-1 text-lg font-bold text-green-600">{formatCurrency(viewingSale.total ?? viewingSale.Total ?? 0)}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="button"
                      onClick={closeViewModal}
                      ref={cancelButtonRef}
                      className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Edit Sale Modal */}
      <Transition.Root show={editingSale !== null} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          initialFocus={cancelButtonRef}
          onClose={closeEditModal}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                        <PencilIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                      </div>
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                        <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                          Edit Sale
                        </Dialog.Title>
                        {editingSale && (
                          <div className="mt-4 space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-2">Sales ID</label>
                              <input
                                type="text"
                                value={editingSale.saleId || editingSale.SaleId}
                                disabled
                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-2">Stock Sold</label>
                              <input
                                type="number"
                                value={editFormData.stockSold}
                                onChange={(e) => handleEditFormChange('stockSold', e.target.value)}
                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5"
                                disabled={isUpdating}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-2">Payment Method</label>
                              <select
                                value={editFormData.paymentMethod}
                                onChange={(e) => handleEditFormChange('paymentMethod', e.target.value)}
                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5"
                                disabled={isUpdating}
                              >
                                <option value="Cash">Cash</option>
                                <option value="POS">POS</option>
                                <option value="Transfer">Transfer</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-2">Sale Date</label>
                              <input
                                type="date"
                                value={editFormData.saleDate}
                                onChange={(e) => handleEditFormChange('saleDate', e.target.value)}
                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5"
                                disabled={isUpdating}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-2">Total Amount</label>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={editFormData.total}
                                onChange={(e) => handleEditFormChange('total', formatNairaInput(e.target.value))}
                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5"
                                disabled={isUpdating}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="button"
                      onClick={saveEdit}
                      disabled={isUpdating}
                      className="inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
                    >
                      {isUpdating ? "Saving..." : "Save"}
                    </button>
                    <button
                      type="button"
                      onClick={closeEditModal}
                      ref={cancelButtonRef}
                      disabled={isUpdating}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto"
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Delete Confirmation Modal */}
      <Transition.Root show={deletingSaleId !== null} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          initialFocus={cancelButtonRef}
          onClose={cancelDelete}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                        <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                      </div>
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                        <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                          Delete Sale
                        </Dialog.Title>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            Are you sure you want to delete this sale? This action cannot be undone.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="button"
                      onClick={confirmDelete}
                      disabled={isDeleting}
                      className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:bg-gray-400 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
                    >
                      {isDeleting ? "Deleting..." : "Delete"}
                    </button>
                    <button
                      type="button"
                      onClick={cancelDelete}
                      ref={cancelButtonRef}
                      disabled={isDeleting}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto"
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}

export default Sales;
